import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, ArrowRight, RotateCw } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useI18n, useLocalizedPath } from "../i18n/I18nContext";
import { toast } from "sonner";

const COPY = {
  en: {
    title: "Check your inbox.",
    sub: "We sent a confirmation link to {email}. Please verify your email before accessing your account.",
    resend: "Resend verification email",
    sending: "Sending…",
    sent: "We just sent a fresh link. Give it a minute.",
    change: "Use a different email",
    support: "Need help? Write to ",
    spamHint: "Can't find it? Check your spam folder — it sometimes hides there.",
  },
  ro: {
    title: "Verifică-ți inbox-ul.",
    sub: "Ți-am trimis un link de confirmare pe {email}. Te rugăm să îți confirmi emailul înainte să accesezi contul.",
    resend: "Retrimite emailul de confirmare",
    sending: "Se trimite…",
    sent: "Tocmai am trimis un link nou. Așteaptă un minut.",
    change: "Folosește alt email",
    support: "Ai nevoie de ajutor? Scrie-ne la ",
    spamHint: "Nu-l găsești? Verifică folderul de spam — uneori se ascunde acolo.",
  },
};

export default function CheckInbox() {
  const { lang } = useI18n();
  const lp = useLocalizedPath();
  const { resendVerification } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const email = location.state?.email || localStorage.getItem("lp_pending_email") || "";
  const c = COPY[lang] || COPY.en;
  const [loading, setLoading] = useState(false);

  const onResend = async () => {
    if (!email) {
      navigate(lp("/register"));
      return;
    }
    setLoading(true);
    const res = await resendVerification(email);
    setLoading(false);
    if (res.ok) toast.success(c.sent);
    else toast.error(res.error || "Error");
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="glass rounded-3xl p-10 md:p-14 w-full max-w-xl"
      >
        <div className="h-14 w-14 rounded-full bg-gold/12 border border-gold/30 flex items-center justify-center">
          <Mail className="h-7 w-7 text-gold" strokeWidth={1.75} />
        </div>
        <h1
          data-testid="check-inbox-title"
          className="mt-8 font-display text-section md:text-section-lg tracking-tight leading-[1.05]"
        >
          {c.title}
        </h1>
        <p className="mt-5 text-cream-muted text-body">
          {c.sub.split("{email}").map((part, i, arr) =>
            i < arr.length - 1 ? (
              <React.Fragment key={i}>
                {part}
                <span className="text-cream font-medium" data-testid="check-inbox-email">{email || "—"}</span>
              </React.Fragment>
            ) : (
              <React.Fragment key={i}>{part}</React.Fragment>
            )
          )}
        </p>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <button
            data-testid="resend-verification"
            onClick={onResend}
            disabled={loading || !email}
            className="btn btn-gold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RotateCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            {loading ? c.sending : c.resend}
          </button>
          <Link to={lp("/register")} className="btn btn-ghost">
            {c.change}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <p className="mt-8 text-caption text-cream-dim">{c.spamHint}</p>
        <p className="mt-2 text-caption text-cream-dim">
          {c.support}
          <a href="mailto:hello@thelifeplot.com" className="text-gold hover:text-gold-hover">
            hello@thelifeplot.com
          </a>
        </p>
      </motion.div>
    </div>
  );
}
